import type { ScheduledPostDto } from '@reelplicate/api-client/model';
import {
  getListScheduledPostsQueryKey,
  useMoveScheduledPost,
} from '@reelplicate/api-client/scheduled-posts';
import { toast } from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { type DragEvent, useState } from 'react';

import { formatWhen } from '@/lib/format-when';

import { FIRST_HOUR, HOUR_COUNT } from './week';

/** Drops land on the quarter hour. */
export const SNAP_MINUTES = 15;
/** Chips sit 3px below their time (see `chipTop`). */
const CHIP_INSET = 3;

/**
 * Where a drop in a week/day column lands: `offsetY` px from the column's top
 * (one px a minute), less where the chip was grabbed, snapped to the quarter
 * hour and kept inside the 8 AM – 9 PM grid.
 */
export function dropTime(day: Date, offsetY: number, grabOffset: number): Date {
  const minutes =
    Math.round((offsetY - grabOffset - CHIP_INSET) / SNAP_MINUTES) * SNAP_MINUTES;
  const clamped = Math.min(Math.max(minutes, 0), HOUR_COUNT * 60 - SNAP_MINUTES);
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), FIRST_HOUR, clamped);
}

/** `start`'s time of day on `day` (Month view keeps the time). */
export function moveToDay(start: Date, day: Date): Date {
  return new Date(
    day.getFullYear(),
    day.getMonth(),
    day.getDate(),
    start.getHours(),
    start.getMinutes(),
  );
}

/** Only the future takes a post (the server says the same). */
export const canDrop = (at: Date, now: Date) => at.getTime() > now.getTime();

/** What the grids need to drag posts around. */
export type CalendarDrag = {
  /** The post being dragged, and how far down its chip it was grabbed (px). */
  dragging: { id: string; start: Date; grabOffset: number } | null;
  /** Where it would land now; null over somewhere it can't go. */
  target: Date | null;
  start: (event: DragEvent, post: { id: string; start: Date }) => void;
  end: () => void;
  /** Hovering `at` (null: not a place to drop): accepts the drop when it's allowed. */
  over: (event: DragEvent, at: Date | null) => void;
  /** Dropped at `at` (worked out like `over`). */
  drop: (event: DragEvent, at: Date | null) => void;
};

/**
 * Dragging a scheduled post to another time: the chip moves at once, the
 * server is told, and the toast offers Undo. A refused move snaps back (the
 * error is toasted by the shared mutation handler).
 */
export function useDragMove(workspaceId: string, now: Date): CalendarDrag {
  const queryClient = useQueryClient();
  const { mutateAsync } = useMoveScheduledPost();
  const [dragging, setDragging] = useState<CalendarDrag['dragging']>(null);
  const [target, setTarget] = useState<Date | null>(null);
  const key = getListScheduledPostsQueryKey(workspaceId);

  const move = async (id: string, from: Date, to: Date, undo = false) => {
    await queryClient.cancelQueries({ queryKey: key });
    const before = queryClient.getQueriesData<ScheduledPostDto[]>({ queryKey: key });
    queryClient.setQueriesData<ScheduledPostDto[]>({ queryKey: key }, (posts) =>
      posts?.map((p) => (p.id === id ? { ...p, scheduledAt: to.toISOString() } : p)),
    );
    try {
      await mutateAsync({
        id: workspaceId,
        postId: id,
        data: { scheduledAt: to.toISOString() },
      });
    } catch {
      for (const [queryKey, data] of before) queryClient.setQueryData(queryKey, data);
      return;
    } finally {
      void queryClient.invalidateQueries({ queryKey: key });
    }
    if (undo) toast.success(`Moved back to ${formatWhen(to)}`);
    else
      toast.success(`Moved to ${formatWhen(to)}`, {
        action: { label: 'Undo', onClick: () => void move(id, to, from, true) },
      });
  };

  const end = () => {
    setDragging(null);
    setTarget(null);
  };

  return {
    dragging,
    target,
    start: (event, post) => {
      event.dataTransfer.effectAllowed = 'move';
      // Firefox only starts a drag that carries data.
      event.dataTransfer.setData('text/plain', post.id);
      const box = event.currentTarget.getBoundingClientRect();
      setDragging({ ...post, grabOffset: event.clientY - box.top });
    },
    end,
    over: (event, at) => {
      const ok = at !== null && canDrop(at, now);
      if (ok) {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
      }
      const next = ok ? at : null;
      if (next?.getTime() !== target?.getTime()) setTarget(next);
    },
    drop: (event, at) => {
      event.preventDefault();
      const post = dragging;
      end();
      if (!post || !at || !canDrop(at, now) || at.getTime() === post.start.getTime())
        return;
      void move(post.id, post.start, at);
    },
  };
}
